import React, { useState, useEffect } from "react";
import UserProfileStyles from "./UserProfilePage.module.css";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserEdit, faSave, faPlus } from "@fortawesome/free-solid-svg-icons";
import ClipLoader from "react-spinners/ClipLoader";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";

const UserProfilePage = () => {
  const [role, setRole] = useState(localStorage.getItem("userRole") || "User");
  const [userId, setUserId] = useState(localStorage.getItem("adminId") || "");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    name: "",
    image: "",
    email: "",
    phone: ""
  });

  const [editMode, setEditMode] = useState(false);
  const [updatedImageFile, setUpdatedImageFile] = useState(null);

  const [phoneNumber, setPhoneNumber] = useState(userData.phone || "");
  const [phoneError, setPhoneError] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchUserInfo = async () => {
      setLoading(true);
      setSuccessMessage("");
      console.log("role,  userId: ", role, userId);
      try {
        const response = await axios.get(
          `${BACKEND_URL}/admin/profile?userId=${userId}&role=${role}`
        );

        if (response.status === 200) {
          const data = response.data;
          if (role === "User") {
            setUserData((prev) => ({
              ...prev,
              firstName: data.firstName || data.name || "",
              lastName: data.lastName || "",
              image: data.image || "",
              email: data.email || "",
              phone: data.phoneNumber || ""
            }));
            setPhoneNumber(data.phoneNumber || "");
          } else {
            // For Admin or Clubowner
            setUserData((prev) => ({
              ...prev,
              firstName: data.name || "",
              lastName: "",
              image: data.image || "",
              email: data.email || "",
              phone: data.phoneNumber || ""
            }));
            setPhoneNumber(data.phoneNumber || "");
          }
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
      setLoading(false);
    };
    fetchUserInfo();
  }, [BACKEND_URL, role, userId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/\s/g, ""); // Remove all spaces
    setUserData((prev) => ({ ...prev, [name]: sanitizedValue }));
    setSuccessMessage(""); // Clear success message when editing
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setUpdatedImageFile(e.target.files[0]);
      const reader = new FileReader();
      reader.onload = (ev) => {
        setUserData((prev) => ({ ...prev, image: ev.target.result }));
      };
      reader.readAsDataURL(e.target.files[0]);
      setSuccessMessage(""); // Clear success message when changing image
    }
  };

  const handleSave = async () => {
    // Validate phone number
    if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
      setPhoneError("Please enter a valid phone number.");
      return;
    }

    setLoading(true);
    setSuccessMessage("");
    setPhoneError("");
    try {
      const formData = new FormData();
      if (role === "User") {
        formData.append("firstName", userData.firstName);
        formData.append("lastName", userData.lastName);
      } else {
        formData.append("name", userData.firstName);
      }
      formData.append("userId", userId);
      formData.append("role", role);
      formData.append("email", userData.email);
      formData.append("phoneNumber", phoneNumber); // Use phoneNumber state

      if (updatedImageFile) {
        formData.append("image", updatedImageFile);
      }

      const response = await axios.put(
        `${BACKEND_URL}/admin/profile`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        }
      );
      if (response.status === 200) {
        setEditMode(false);
        setSuccessMessage("Profile updated successfully.");
        // Update userData.phone to reflect the new phone number
        setUserData((prev) => ({ ...prev, phone: phoneNumber }));
        // Optionally, update localStorage or other states if needed
        localStorage.setItem("phoneNumber", phoneNumber);
      }
    } catch (error) {
      console.error("Error updating user info:", error);
      setPhoneError("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
      setUpdatedImageFile(null);
    }
  };

  // Helper function to validate phone number
  const isValidPhoneNumber = (number) => {
    // You can use the validator from react-phone-number-input or any other method
    // For simplicity, we'll check if the number has at least 10 digits
    const digits = number.replace(/\D/g, "");
    return digits.length >= 10;
  };

  if (loading && !editMode) {
    return (
      <div className={UserProfileStyles.loadingContainer}>
        <ClipLoader color={"#00338d"} size={80} />
      </div>
    );
  }

  return (
    <div className={UserProfileStyles.profileContainer}>
      <div className={UserProfileStyles.header}>
        <h2>Personal Information</h2>
        <button
          className={UserProfileStyles.editButton}
          onClick={() => {
            setEditMode(!editMode);
            setSuccessMessage("");
            setPhoneError("");
            // Initialize phoneNumber with current phone
            setPhoneNumber(userData.phone);
          }}
        >
          <FontAwesomeIcon icon={faUserEdit} /> {editMode ? "Cancel" : "Edit"}
        </button>
      </div>
      {successMessage && (
        <div style={{ color: "green", marginBottom: "10px" }}>
          {successMessage}
        </div>
      )}
      {phoneError && (
        <div style={{ color: "red", marginBottom: "10px" }}>{phoneError}</div>
      )}
      <div className={UserProfileStyles.profileCard}>
        <div className={UserProfileStyles.imageSection}>
          <div
            className={UserProfileStyles.imageWrapper}
            style={{ position: "relative", overflow: "visible" }}
          >
            {userData.image ? (
              <img
                src={userData.image}
                alt="Profile"
                className={UserProfileStyles.profileImage}
              />
            ) : (
              <div className={UserProfileStyles.profilePlaceholder}>
                No Image
              </div>
            )}
            {editMode && (
              <label
                className={UserProfileStyles.cameraIcon}
                style={{
                  position: "absolute",
                  bottom: "5px",
                  right: "5px",
                  cursor: "pointer",
                  background: "#fff",
                  borderRadius: "50%",
                  padding: "5px"
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className={UserProfileStyles.fileInput}
                  style={{ display: "none" }}
                />
              </label>
            )}
          </div>
          <p className={UserProfileStyles.roleLabel}>
            {role === "Admin"
              ? "Team Leader - Collaborator"
              : role === "Clubowner"
                ? "Club Owner"
                : "Promoter"}
          </p>
        </div>
        <div className={UserProfileStyles.infoSection}>
          {role === "User" ? (
            <>
              <div className={UserProfileStyles.inputGroup}>
                <label>First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={userData.firstName}
                  disabled={!editMode}
                  onChange={handleInputChange}
                />
              </div>
              <div className={UserProfileStyles.inputGroup}>
                <label>Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={userData.lastName}
                  disabled={!editMode}
                  onChange={handleInputChange}
                />
              </div>
            </>
          ) : (
            <div className={UserProfileStyles.inputGroup}>
              <label>Name</label>
              <input
                type="text"
                name="firstName"
                value={userData.firstName}
                disabled={!editMode}
                onChange={handleInputChange}
              />
            </div>
          )}
          <div className={UserProfileStyles.inputGroup}>
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={userData.email}
              disabled={!editMode}
              onChange={handleInputChange}
            />
          </div>
          <div className={UserProfileStyles.inputGroup}>
            <label>Phone Number</label>
            {editMode ? (
              <PhoneInput
                international
                defaultCountry="US"
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={(value) => {
                  setPhoneNumber(value);
                  setPhoneError("");
                }}
                className={UserProfileStyles.phoneInput}
              />
            ) : (
              <input
                type="text"
                name="phone"
                value={userData.phone}
                disabled
                onChange={handleInputChange}
              />
            )}
          </div>
        </div>
      </div>
      {editMode && (
        <div className={UserProfileStyles.saveContainer}>
          <button
            className={UserProfileStyles.saveButton}
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? (
              <>
                <ClipLoader color="#fff" size={15} /> Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> Save
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserProfilePage;
